// A room's AC–Fan cycle — the phone's room screen draws the CARD, the desktop
// room header a CHIP that opens the same card. Renders NOTHING in a room
// without both an AC and a fan (unless a cycle is somehow running there).
//
//   🌙 AC–Fan cycle                         [ON]
//   ❄ AC on — 18 min left
//   ▓▓▓▓▓▓▓▓░░░░░
//   Next: fan 80% for 20 min · morning 06:00 (in 5 h)
//   [Skip]                                   ⚙
//
// The server runs it (Home Automation api/new/roomCycle.js); this only asks and shows.

import React, { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, IconButton, LinearProgress, Paper, Switch,
  Tooltip, Typography,
} from '@mui/material';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ToysIcon from '@mui/icons-material/Toys';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import SettingsIcon from '@mui/icons-material/Settings';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { useApp } from '../../../core/state/AppContext';
import useRoomCycle from '../../../core/hooks/useRoomCycle';
import {
  duration, nextLine, phaseLine, programLine, progress, withDefaults,
} from '../../../core/roomCycle/roomCycle';
import { saveRoomCycle, startRoomCycle } from '../../../core/api/api';
import { showToast } from '../toast';
import RoomCycleDialog from './RoomCycleDialog';

const PHASE_ICON = {
  ac: <AcUnitIcon fontSize="small" />,
  fan: <ToysIcon fontSize="small" />,
  acwait: <HourglassTopIcon fontSize="small" />,
  morning: <WbTwilightIcon fontSize="small" />,
};

function CycleBody({
  cycle, roomName, userId, roomId, color, flat = false,
}) {
  const [editOpen, setEditOpen] = useState(false);
  const {
    data, busy, secondsLeft, start, stop, skip, save, reload,
  } = cycle;
  const state = data?.state || '';
  const running = !!state;
  const saved = !!data?.settings;
  const settings = withDefaults(data?.settings);

  const toggle = async () => {
    if (running) {
      const e = await stop();
      showToast(e || 'Cycle stopped — AC and fan left as they are', { severity: e ? 'error' : 'success' });
      return;
    }
    if (!saved) { setEditOpen(true); return; }   // nothing chosen yet: the editor first
    const e = await start();
    showToast(e || 'Cycle started', { severity: e ? 'error' : 'success' });
  };

  const onSkip = async () => {
    const e = await skip();
    if (e) showToast(e, { severity: 'error' });
  };

  const onSave = async (s) => {
    const e = await save(s);
    if (!e) showToast('Cycle settings saved', { severity: 'success' });
    return e;
  };
  // Save & start: two calls, then one refresh.
  const onSaveAndStart = async (s) => {
    try {
      await saveRoomCycle(userId, roomId, s);
      await startRoomCycle(userId, roomId);
      await reload();
      showToast('Cycle started', { severity: 'success' });
      return '';
    } catch (e) {
      return e?.response?.data?.error || e?.message || 'Failed';
    }
  };

  const p = progress(secondsLeft, data?.phaseSeconds);
  const morningIn = data?.morningIn;

  return (
    <Paper
      variant={flat ? 'elevation' : 'outlined'}
      elevation={0}
      sx={{ px: 1.25, py: 0.75, borderRadius: 3, bgcolor: flat ? 'transparent' : undefined, color }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NightsStayIcon sx={{ color: running ? 'primary.main' : 'inherit', opacity: running ? 1 : 0.7 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>AC–Fan cycle</Typography>
          {!running && (
            <Typography noWrap sx={{ opacity: 0.75, fontSize: 12 }}>
              {saved ? programLine(settings) : 'Tap to set up: AC and fan take turns'}
            </Typography>
          )}
        </Box>
        {busy && <CircularProgress size={18} />}
        <Tooltip title="Settings">
          <IconButton size="small" onClick={() => setEditOpen(true)} sx={{ color: 'inherit' }} aria-label="cycle settings"><SettingsIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Switch size="small" checked={running} onChange={toggle} disabled={busy || data?.setup === false} inputProps={{ 'aria-label': 'cycle on/off' }} />
      </Box>

      {data?.setup === false && (
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.75 }}>
          Not set up on the server yet.
        </Typography>
      )}

      {running && (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {PHASE_ICON[state]}
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{phaseLine(state, data.settings, secondsLeft)}</Typography>
          </Box>
          {p != null && state !== 'morning' && (
            <LinearProgress variant="determinate" value={p * 100} sx={{ my: 0.5, height: 4, borderRadius: 2 }} />
          )}
          <Typography sx={{ opacity: 0.8, fontSize: 12 }}>
            {[
              nextLine(state, data.settings),
              settings.Morning.On && state !== 'morning' && morningIn != null
                ? `morning ${settings.Morning.StopAt} (in ${duration(morningIn)})` : '',
            ].filter(Boolean).join(' · ')}
          </Typography>
          {data.note && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.65 }}>{data.note}</Typography>
          )}
          {(state === 'ac' || state === 'fan' || state === 'morning') && (
            <Button
              size="small"
              startIcon={<SkipNextIcon />}
              onClick={onSkip}
              disabled={busy}
              sx={{ mt: 0.25, py: 0, minHeight: 0, textTransform: 'none', color: 'inherit' }}
            >
              {state === 'morning' ? 'Fan off now' : 'Skip to next'}
            </Button>
          )}
        </Box>
      )}

      {!running && data?.note && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>{`Last: ${data.note}`}</Typography>
      )}

      <RoomCycleDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        data={data}
        onSave={onSave}
        onSaveAndStart={running ? null : onSaveAndStart}
        userId={userId}
        roomId={roomId}
        roomName={roomName}
      />
    </Paper>
  );
}

/**
 * props: roomId, roomName, compact (desktop chip), color, sx
 */
export default function RoomCycleCard({
  roomId, roomName, compact = false, color, sx,
}) {
  const { userId, realUserId } = useApp();
  const who = realUserId || userId;
  const cycle = useRoomCycle(who, roomId);
  const [open, setOpen] = useState(false);
  const { data, secondsLeft } = cycle;
  if (!data) return null;
  const running = !!data.state;
  if (!running && !data.available) return null;

  if (compact) {
    const label = running
      ? `${{ ac: 'AC', fan: 'Fan', acwait: 'AC waits', morning: 'Morning' }[data.state] || 'Cycle'}${secondsLeft != null ? ` · ${duration(secondsLeft)}` : ''}`
      : 'AC–Fan cycle';
    return (
      <>
        <Tooltip title={running ? phaseLine(data.state, data.settings, secondsLeft) : 'AC and fan take turns — set up or start'}>
          <Chip
            size="small"
            icon={running ? PHASE_ICON[data.state] : <NightsStayIcon fontSize="small" />}
            label={label}
            color={running ? 'primary' : 'default'}
            variant={running ? 'filled' : 'outlined'}
            onClick={() => setOpen(true)}
            sx={sx}
          />
        </Tooltip>
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
          <DialogContent sx={{ p: 1 }}>
            <CycleBody cycle={cycle} roomName={roomName} userId={who} roomId={roomId} flat />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box sx={sx}>
      <CycleBody cycle={cycle} roomName={roomName} userId={who} roomId={roomId} color={color} />
    </Box>
  );
}
